import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import React, { useCallback } from "react"
import { FormProvider } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { usePricingPackage } from "../hooks/usePricingPackage"

import { PricingPackageTable } from "./PricingPackageTable"

import { Button } from "@/components/common/Button"
import { formattedTierData } from "@/constants/pricingTiers"
import type {
  CreatePricingPackageFailure,
  CreatePricingPackageInput,
  PricingPackage,
  Product,
  UpdatePricingPackageFailure,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { AddPricingValues } from "@/screens/Products/components/PricingTiers/AddPricingSchema"
import AddPricingTiersModal from "@/screens/Products/components/PricingTiers/AddPricingTiersModal"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface FuturePricingPackageProps {
  product: Product
  sourceId: string
  pricingList: PricingPackage[]
  openFutureModal?: boolean
  isEmptyFuturePricing?: boolean
  reexecuteQueryWithReset: () => void
  onSubmit?: (showAddMoreModal: boolean) => void
  onFuturePricingSubmit?: (isOpen: boolean) => void
}

export const FuturePricingPackage: React.FC<FuturePricingPackageProps> = ({
  product,
  sourceId,
  pricingList = [],
  openFutureModal = false,
  reexecuteQueryWithReset,
  isEmptyFuturePricing = false,
  onSubmit,
  onFuturePricingSubmit,
}) => {
  const notify = useNotificationsStore(useShallow(({ enqueueNotification }) => enqueueNotification))
  const { createPricingTier, methods, updatePricingTier, onPricingEditClick } = usePricingPackage()
  const { reset, getValues } = methods

  const handleSubmit = useCallback(
    async (data: AddPricingValues) => {
      const pricingPackageId = getValues("pricingPackageId")
      const { pricingPackage, pricingTiers } = formattedTierData({
        ...data,
        companyId: product.company?.companyId,
        productId: product.productId,
        sourceCompanyLocationAssociationId: sourceId,
      })
      const { error, data: result } = await (pricingPackageId
        ? updatePricingTier({
            input: {
              pricingPackageToUpdate: {
                ...pricingPackage,
                sourceCompanyLocationAssociationId: sourceId,
                companyId: product.company?.companyId,
                pricingPackageId,
              },
              pricingTiersToUpdate: pricingTiers,
            },
          })
        : createPricingTier({
            input: {
              pricingPackageToCreate: {
                ...pricingPackage,
                sourceCompanyLocationAssociationId: sourceId,
                companyId: product.company?.companyId,
              },
              pricingTiersToCreate: pricingTiers,
            } as CreatePricingPackageInput,
          }))

      const failure = pricingPackageId
        ? (result?.pricingPackage?.update as UpdatePricingPackageFailure)?.error
        : (result?.pricingPackage?.create as CreatePricingPackageFailure)?.error
      if (error || failure) {
        notify({
          type: NotificationType.Error,
          message: `Pricing and Shipping terms ${pricingPackageId ? "update" : "creation"} failed`,
        })
      } else {
        reexecuteQueryWithReset()
        if (pricingPackageId) {
          notify({ type: NotificationType.Success, message: "Pricing and Shipping terms updated successfully" })
        } else {
          onSubmit?.(!pricingPackageId)
        }
      }
      onFuturePricingSubmit?.(false)
      reset()
    },
    [
      product,
      sourceId,
      updatePricingTier,
      createPricingTier,
      onFuturePricingSubmit,
      notify,
      reexecuteQueryWithReset,
      onSubmit,
      getValues,
      reset,
    ]
  )

  if (isEmptyFuturePricing) {
    return null
  }

  return (
    <>
      {pricingList.map((pricingPackage) => (
        <PricingPackageTable
          key={pricingPackage.pricingPackageId}
          title='Future Pricing'
          pricingDetails={pricingPackage} // Passing only the first pricing package as we are showing only the current pricing package
          onEditClick={(uomName) => {
            onPricingEditClick(pricingPackage, uomName)
            onFuturePricingSubmit?.(true)
          }}
        />
      ))}
      <Box className='flex justify-end'>
        <Button
          variant='primary'
          appearance='text'
          startIcon={<AddOutlined />}
          onClick={() => {
            methods.reset()
            onFuturePricingSubmit?.(true)
          }}
        >
          {pricingList.length === 0 ? "Future Pricing" : "Additional Pricing"}
        </Button>
      </Box>
      <FormProvider {...methods}>
        <AddPricingTiersModal
          open={openFutureModal}
          product={product}
          onClose={() => {
            onFuturePricingSubmit?.(false)
          }}
          onSubmit={handleSubmit}
        />
      </FormProvider>
    </>
  )
}
